/**
 * A aba Exame da mesa única (E201.F1, ADR-0144; E205.F2, ADR-0147 §2–§3).
 *
 * - A barra da aba (`BarraDoExame`): a troca de saída e o selo do texto à esquerda; "Documentos" e o
 *   botão "Exame" (prévia, baixar, ZIP) à direita. E207.F5.T03 (owner, 01/11): "Pacote completo ·
 *   Ver o pacote" saiu da barra — o botão "Exame" já oferece o ZIP do pacote.
 * - No alto, só com pendência: o banco do caso a informar (a plataforma), a divergência mantida na
 *   abertura, o card do pacote enquanto prepara, falhou ou bloqueou.
 * - O documento uma seção por vez (`useSecaoDoExame`), com o índice das seções à esquerda, recolhível,
 *   e "Anterior"/"Próxima" no alto e no fim. A edição por bloco, as notas, o painel do dado e "Alterar
 *   na mesa" não mudam. E216.F6.T01: o índice e a navegação do alto ficam presos ao rolar o exame.
 */
import type { AxiosInstance } from 'axios';
import { useState } from 'react';

import { DocumentoDaMesa, type DocumentoDaMesaProps } from '@/components/DocumentoDaMesa';
import { TrocaDeSaida } from '@/components/DocumentoDaMesa/DocumentoDaMesa';
import { SeloDoTexto } from '@/components/DocumentoDaMesa/SeloDoTexto';
import { DivergenciaMantidaNaMesa } from '@/components/ExameEletronico/CcbDaMesa';
import { InformarOBanco } from '@/components/ExameEletronico/InformarOBanco';
import type { useMesaEletronica } from '@/components/MesaEletronica/useMesaEletronica';
import {
  pacoteSemPendencia,
  PreparacaoDoPacoteLido,
} from '@/components/PreparacaoDoPacote/PreparacaoDoPacote';
import { ptBR } from '@/i18n/pt-BR';
import type { PreparacaoDoPacote as LeituraEAcoesDoPacote } from '@/lib/usePreparacaoDoPacote';

import { BarraDoExame } from './BarraDoExame';
import { ColunaDoIndice, NavegacaoDasSecoes } from './IndiceDasSecoes';
import type { AbaDoPainel } from './usePainelDaMesa';
import type { SecaoDoExame } from './useSecaoDoExame';

const ts = ptBR.documentoDaMesa.secoes;

type Mesa = ReturnType<typeof useMesaEletronica>;

export interface AreaDoExameProps {
  client: AxiosInstance;
  caseId: string;
  mesa: Mesa;
  processando: boolean;
  fotograficaEmAndamento: boolean;
  /** E203.F5.T07: o pacote lido uma vez na mesa (a prévia ao lado e o card usam a mesma leitura). */
  pacote: LeituraEAcoesDoPacote;
  /** O documento do centro: a leitura, a saída mostrada e a troca de saída. */
  documento: DocumentoDaMesaProps;
  /** E205.F2: a seção à vista e os links das outras. */
  secao: SecaoDoExame;
  onMudou: () => void;
  /** O painel ao lado: aberto em qual aba (`null`, fechado), o botão que o alterna e a prévia. */
  painel: AbaDoPainel | null;
  onPainel: (aba: AbaDoPainel) => void;
  onPrevia: () => void;
}

function SecoesDoDocumento({
  secao,
  documento,
}: {
  secao: SecaoDoExame;
  documento: DocumentoDaMesaProps;
}): JSX.Element {
  const [indiceAberto, setIndiceAberto] = useState(true);
  const comIndice = secao.secoes.length > 1;
  const navegacao = (lugar: 'alto' | 'fim') => (
    <NavegacaoDasSecoes
      secoes={secao.secoes}
      ativa={secao.ativa}
      linkDa={secao.linkDa}
      lugar={lugar}
    />
  );
  return (
    <div
      className={`mt-sc-4 grid gap-sc-4 ${comIndice && indiceAberto ? 'lg:grid-cols-[12rem_minmax(0,1fr)]' : ''}`}
    >
      {comIndice ? (
        <ColunaDoIndice
          secoes={secao.secoes}
          ativa={secao.ativa}
          linkDa={secao.linkDa}
          aberto={indiceAberto}
          onAlternar={() => setIndiceAberto((a) => !a)}
        />
      ) : null}
      <div className="min-w-0">
        {secao.naoTrocou ? (
          <p
            role="alert"
            className="mb-sc-3 border border-danger-500 bg-danger-50 p-sc-2 text-body-sm text-danger-700"
            data-testid="secao-nao-trocou"
          >
            {ts.naoTrocou}
          </p>
        ) : null}
        <DocumentoDaMesa
          {...documento}
          comRisco={false}
          porSecao={
            secao.ativa === null
              ? null
              : { ativa: secao.ativa, alto: navegacao('alto'), fim: navegacao('fim') }
          }
        />
      </div>
    </div>
  );
}

/** A esquerda da barra: a troca de saída (PARTE com risco) e o selo do texto. */
function EsquerdaDaBarra({ documento }: { documento: DocumentoDaMesaProps }): JSX.Element {
  const dados = documento.leitura.dados;
  const edicao = dados?.assinado === true ? null : (documento.edicao ?? null);
  return (
    <>
      {documento.comRisco ? (
        <TrocaDeSaida saida={documento.saida} onSaida={documento.onSaida} />
      ) : null}
      {dados !== null ? <SeloDoTexto edicao={edicao} /> : null}
    </>
  );
}

export function AreaDoExame(props: AreaDoExameProps): JSX.Element {
  const { client, caseId, mesa, onMudou, documento } = props;
  // E203.F5.T05: completo e sem pendência, o pacote não ocupa a tela; o card fica aberto enquanto
  // prepara, falhou ou bloqueou. E207.F5.T03: sem a linha "Pacote completo · Ver o pacote".
  const leitura = props.pacote.leitura;
  const cardDoPacote = !(leitura.estado === 'pronto' && pacoteSemPendencia(leitura.dados));
  return (
    <div data-testid="mesa-area-exame">
      {mesa.ccbs !== null ? (
        <BarraDoExame
          client={client}
          caseId={caseId}
          pecas={mesa.ccbs}
          temProtocolo={mesa.protocolo !== null}
          emProcessamento={props.processando}
          bancoDoCaso={mesa.bancoDoCaso}
          analiseFotograficaEmAndamento={props.fotograficaEmAndamento}
          esquerda={<EsquerdaDaBarra documento={documento} />}
          painel={props.painel}
          onPainel={props.onPainel}
          onPrevia={props.onPrevia}
          onAtualizarPacote={props.pacote.atualizar}
        />
      ) : null}
      <InformarOBanco caseId={caseId} bancoDoCaso={mesa.bancoDoCaso} onInformado={onMudou} />
      {mesa.divergenciaMantida !== null ? (
        <div className="mt-sc-4">
          <DivergenciaMantidaNaMesa divergencia={mesa.divergenciaMantida} />
        </div>
      ) : null}
      {/* Escondido, e não desmontado: o aviso de mudança do pacote segue relendo a mesa. */}
      <div hidden={!cardDoPacote} data-testid="pacote-card">
        <PreparacaoDoPacoteLido
          client={client}
          caseId={caseId}
          tela="mesa"
          onMudou={onMudou}
          preparacao={props.pacote}
        />
      </div>
      <SecoesDoDocumento secao={props.secao} documento={documento} />
    </div>
  );
}
